/**
 * Applicant-facing document check presentation (owner addition #2).
 *
 * PURE + dependency-free: imported by the server review core (which
 * stamps `applicantVerdict` on the document record) AND by the client
 * (which derives what to render). One mapping, both sides.
 *
 * The applicant sees the SYSTEM's verdict in plain language:
 *   - 'checking'     — the automated scan is running (stamped from the
 *                      pipeline's real claim state, never a fake timer)
 *   - 'approved'     — the system's pass verdict (internal
 *                      SystemPreApproved), presented as Approved
 *   - 'rejected'     — the system's rejection, with ONE brief
 *                      applicant-safe sentence of explanation
 *   - 'under_review' — uncertain / unreadable / AI did not run / waiting
 *                      for a human: an honest "under review" state
 *
 * GATE SAFETY: this is presentation only. The workflow documents gate
 * (server/workflows/snapshot.ts) still counts ONLY full 'Approved'
 * (plus its legacy spellings) — an applicantVerdict of 'approved' does
 * NOT satisfy it, exactly like SystemPreApproved never has.
 */

export type ApplicantVerdictState = 'checking' | 'approved' | 'rejected' | 'under_review';

export interface ApplicantDocumentVerdict {
  state: ApplicantVerdictState;
  /**
   * One brief applicant-safe sentence: the reason for 'rejected', and
   * for 'approved' the one-sentence verified-facts note (owner,
   * 2026-10-16 — approval notes are capped at exactly one sentence on
   * the applicant surface, however long the model's reason arrived).
   */
  reason?: string;
  at: string;
}

/**
 * Narrow safety net for the applicant-facing note (owner, 2026-10-16):
 * the review prompt instructs the model to write as the reviewing
 * office and never disclose AI authorship — but if a model emits a
 * leading AI self-disclosure clause anyway ("As an AI…", "The AI
 * review found that…", "Our automated system flagged this document
 * because…"), strip JUST that leading clause before the note is stored
 * or shown. Nothing else is rewritten: the fact-bearing remainder is
 * returned untouched (the one-sentence cap in firstApplicantSentence
 * still applies on top).
 */
const AI_DISCLOSURE_PREFIX_PATTERNS: RegExp[] = [
  // "As an AI, …" / "As an AI language model, …" — clause through the comma.
  /^as an ai(?:\s+language model)?\b[^.!?]*?,\s*/i,
  // "As an AI …" with no comma — the bare self-reference only.
  /^as an ai(?:\s+language model)?\s+/i,
  // "The AI review found that …" / "Our automated system detected that …"
  /^(?:the|this|our|an)\s+(?:ai|automated)\s+(?:review|check|system|tool|analysis)\s+(?:has\s+)?(?:found|determined|shows?|indicates?|concluded|flagged|revealed|detected)\s+that\s+/i,
  // "Our automated system flagged this document because …"
  /^(?:the|this|our|an)\s+(?:ai|automated)\s+(?:review|check|system|tool|analysis)\s+(?:flagged|detected)\s+(?:the|this|your)\s+document\s+because\s+/i,
  // "Our automated system could not verify the document because …"
  /^(?:the|this|our|an)\s+(?:ai|automated)\s+(?:review|check|system|tool|analysis)\s+(?:could not|was unable to|was not able to|cannot|can't)\s+(?:verify|confirm|validate|process|read)\s+(?:the|this|your)\s+document\s+because\s+/i,
  // Standalone first-person preamble sentence: "I reviewed your document."
  /^i(?:'ve|\s+have)?\s+(?:reviewed|checked|analysed|analyzed|examined|assessed)\s+(?:your|the|this)\s+\w+\s*[.!?]\s*/i,
];

export function stripAiDisclosurePrefix(reason?: string | null): string {
  let text = String(reason || '').trim();
  // A strip can expose a further disclosure clause (e.g. "As an AI, I
  // reviewed your document." leaves a first-person preamble sentence),
  // so re-apply until nothing matches — bounded, each pass must shrink.
  for (let pass = 0; pass < 4 && text; pass++) {
    const before = text;
    for (const pattern of AI_DISCLOSURE_PREFIX_PATTERNS) {
      text = text.replace(pattern, '').trimStart();
    }
    if (text === before) break;
  }
  return text;
}

/** First sentence only, markup stripped, AI-disclosure prefix removed, length-capped. */
export function firstApplicantSentence(reason?: string | null): string {
  const cleaned = String(reason || '')
    .replace(/[*_`#>\[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return '';
  const clean = stripAiDisclosurePrefix(cleaned);
  if (!clean) return '';
  const sentence = clean.split(/(?<=[.!?])\s+/)[0] || clean;
  const capped = sentence.length > 180 ? `${sentence.slice(0, 177).trim()}…` : sentence;
  // Stripping a mid-sentence prefix can expose a lowercase start; the
  // note stands as its own sentence, so restore sentence case — only
  // when a disclosure strip actually changed the text.
  return clean !== cleaned ? capped.replace(/^[a-z]/, (c) => c.toUpperCase()) : capped;
}

/**
 * The applicant verdict for a completed system judgment. Input is the
 * consolidated verdict's public shape (structural — no core import).
 */
export function applicantVerdictFromSystemVerdict(verdict: {
  aiRan: boolean;
  suggestedStatus: 'SystemPreApproved' | 'PendingAdminReview' | 'Rejected';
  identityStatus?: string;
  reason?: string;
}): ApplicantDocumentVerdict {
  const at = new Date().toISOString();
  if (!verdict.aiRan) return { state: 'under_review', at };
  if (verdict.suggestedStatus === 'SystemPreApproved') {
    const note = firstApplicantSentence(verdict.reason);
    return note ? { state: 'approved', reason: note, at } : { state: 'approved', at };
  }
  if (verdict.suggestedStatus === 'Rejected') {
    const reason =
      verdict.identityStatus === 'mismatch'
        ? "The details on this document don't match the details on your application."
        : firstApplicantSentence(verdict.reason) || 'This document could not be accepted.';
    return { state: 'rejected', reason, at };
  }
  return { state: 'under_review', at };
}

/**
 * The applicant verdict after a HUMAN (or admin-applied) status decision.
 * Returns null for statuses that carry no applicant verdict change
 * (e.g. FurtherReviewRequested — the rejected verdict stays visible
 * while the further review is open).
 */
export function applicantVerdictForHumanStatus(
  status: string,
  rejectionNote?: string | null
): ApplicantDocumentVerdict | null {
  const at = new Date().toISOString();
  switch (status) {
    case 'Approved':
    case 'Approved Admin':
    case 'Verified':
    case 'SystemPreApproved':
      return { state: 'approved', at };
    case 'Rejected': {
      const reason = firstApplicantSentence(rejectionNote) || 'This document was not accepted.';
      return { state: 'rejected', reason, at };
    }
    case 'Pending':
    case 'PendingAdminReview':
    case 'PreVetted':
      return { state: 'under_review', at };
    default:
      return null;
  }
}

export type ApplicantDocDisplayState =
  | ApplicantVerdictState
  | 'further_review_requested'
  | 'none';

export interface ApplicantDocDisplay {
  state: ApplicantDocDisplayState;
  reason?: string;
}

/**
 * What the applicant's documents UI renders for one document, derived
 * from the record's REAL fields (internal status + server-stamped
 * applicantVerdict). 'none' = legacy record with nothing to present
 * beyond its internal status (callers fall back to their old rendering).
 */
export function deriveApplicantDocDisplay(doc: {
  status?: string;
  applicantVerdict?: ApplicantDocumentVerdict | null;
  rejectionNote?: string;
}): ApplicantDocDisplay {
  if (!doc) return { state: 'none' };
  if (doc.status === 'FurtherReviewRequested') return { state: 'further_review_requested' };
  if (doc.status === 'Approved' || doc.status === 'Approved Admin' || doc.status === 'Verified') {
    return { state: 'approved' };
  }
  if (doc.status === 'Rejected') {
    return {
      state: 'rejected',
      reason:
        firstApplicantSentence(doc.rejectionNote) ||
        (doc.applicantVerdict?.state === 'rejected' ? doc.applicantVerdict.reason : undefined) ||
        'This document was not accepted.',
    };
  }
  const verdict = doc.applicantVerdict;
  if (verdict && verdict.state) {
    // The approval note is re-capped here as well, so the applicant
    // surface shows exactly one sentence even for a verdict stamped
    // before the cap existed; the stamped rejected reason was already
    // capped at stamp time and passes through unchanged.
    const reason =
      verdict.state === 'rejected'
        ? verdict.reason
        : verdict.state === 'approved'
          ? firstApplicantSentence(verdict.reason) || undefined
          : undefined;
    return { state: verdict.state, reason };
  }
  // No server-stamped verdict (record predates the pipeline stamping):
  // present the internal resting states honestly, never as a pass.
  if (doc.status === 'SystemPreApproved') return { state: 'approved' };
  if (doc.status === 'PendingAdminReview' || doc.status === 'PreVetted') return { state: 'under_review' };
  if (doc.status === 'Pending') return { state: 'under_review' };
  return { state: 'none' };
}

/**
 * Pure gate for POST /api/documents/request-further-review — kept here
 * so the endpoint stays thin and the rules are unit-testable:
 * only the document's OWN applicant, only when the applicant-visible
 * verdict is Rejected, and only one open request per document (a doc
 * already in FurtherReviewRequested has one open).
 */
export function evaluateFurtherReviewRequest(args: {
  callerUid?: string | null;
  applicationUserId?: string | null;
  docStatus?: string | null;
  applicantVerdictState?: string | null;
}): { allowed: boolean; reason?: string } {
  if (!args.callerUid || !args.applicationUserId || args.callerUid !== args.applicationUserId) {
    return { allowed: false, reason: 'Only the applicant who owns this document can request a further review.' };
  }
  if (args.docStatus === 'FurtherReviewRequested') {
    return { allowed: false, reason: 'A further review has already been requested for this document.' };
  }
  if (args.applicantVerdictState !== 'rejected') {
    return { allowed: false, reason: 'A further review can only be requested when the document check did not pass.' };
  }
  return { allowed: true };
}
